import * as _ from 'lodash';
import * as hash from 'object-hash';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { Dispatch, connect } from "react-redux";
import { AppActionCreators, IAppState } from '@nkr/store/application';
import { IBaseResponse, IFileUpload } from '@nkr/models/index';
import {
    FileActionCreators, IAntibodyScreeningResponse, IAntibodyScreeningResultResponse, IApplicationState,
    IRecipientProfileRecordsPostResponse, IRecipientProfileRecordsResponse, RecipientActionCreators
} from '@nkr/store/index';
import { FileDocumentType } from '@nkr/enums/document-type';
import { PatientType } from '@nkr/enums/index';
import { toast } from 'react-toastify';
import { downloadFile } from '@nkr/services/download-file';
import { ProfileWrapper } from '../styles';
import { AfterAction, RecipientProfileContext } from '../models';
import { initialState, recordsReducer } from './state';
import Content from './content';
import AntibodyScreeningDetails from './antibody-screening-details';
import Transplanted from './transplanted';
import DuplicateRecordModal from '@nkr/components/duplicate-record-modal';

interface IOwnProps {
    saveRef: MutableRefObject<any>
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getRecordsData: (centerId: number, recipientId: number) => Promise<IRecipientProfileRecordsResponse>,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    uploadFile: (centerId: number, recipientId: number, docType: number, file: IFileUpload) => Promise<IRecipientProfileRecordsPostResponse>,
    getAntibodyScreenings: (centerId: number, recipientId: number) => Promise<IAntibodyScreeningResponse>,
    getAntibodyScreeningResult: (centerId: number, recipientId: number, screeningId: number) => Promise<IAntibodyScreeningResultResponse>,
    getAntibodyScreeningPdfFile: (centerId: number, recipientId: number, orderId: number) => Promise<any>,
    getAntibodyScreeningRawFile: (centerId: number, recipientId: number, orderId: number) => Promise<any>,
    processAntibodyScreeningOverride: (centerId: number, recipientId: number, screeningId: number) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps;

const Records = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(recordsReducer, initialState);
    const [showDuplicateRecord, setShowDuplicateRecord] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState<string>('');

    useEffect(() => {
        getData();
    }, []);

    const handleDuplicateError = (error: any): boolean => {
        if (error.response.status != 409) {
            return false;
        }

        setDuplicateMessage(error.response.data);
        setShowDuplicateRecord(true);

        return true;
    };

    const handleCloseDuplicateRecord = () => {
        setShowDuplicateRecord(false);
    };

    const getData = () => {
        props.showLoading('Getting records, one moment please...').then(() => {
            Promise.all([
                props.getRecordsData(context.centerId, context.recipientId),
                props.getAntibodyScreenings(context.centerId, context.recipientId)
            ])
                .then(([dataResponse, antibodyResponse]) => {
                    if (dataResponse.data.success && antibodyResponse.data.success) {
                        let data = dataResponse.data.results;

                        dispatch({
                            type: 'afterLoad',
                            value: {
                                data,
                                screenings: antibodyResponse.data.results,
                                hash: hash(data),
                            }
                        });
                    }
                })
                .catch(error => {
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleFileUpload = (docType: FileDocumentType, file: File) => {
        let pendingUploads = _.cloneDeep(state.files);

        let newFile = [file] as File[];
        let fileExists = pendingUploads.find(x => x.docType == docType);

        if (fileExists) {
            pendingUploads.map(x => {
                if (x.docType == docType) {
                    x.file = newFile
                }
            });
        } else {
            let tmpFileData = {
                docType: docType,
                file: newFile
            } as IFileUpload;

            pendingUploads.push(tmpFileData);
        }

        dispatch({
            type: 'updateFiles',
            value: pendingUploads
        });

        context.updateHasChanges(pendingUploads.length > 0);
    };

    const handleRemoveFile = (docType: FileDocumentType) => {
        let newUploadsList = _.cloneDeep(state.files).filter(x => x.docType != docType);

        dispatch({
            type: 'updateFiles',
            value: newUploadsList
        });

        context.updateHasChanges(newUploadsList.length > 0);
    };

    const handleGetPdfFile = (recipientId: number, orderId: number) => {
        props.getAntibodyScreeningPdfFile(context.centerId, recipientId, orderId)
            .then((response: any) => {
                downloadFile(response.data, `${orderId}-results.pdf`, 'application/pdf');
            });
    };

    const handleGetRawFile = (orderId: number) => {
        props.getAntibodyScreeningRawFile(context.centerId, context.recipientId, orderId).then((response: any) => {
            downloadFile(response.data, `${orderId}-results.csv`, 'text/csv');
        });
    };

    const handleViewFile = (docType: FileDocumentType) => {
        props.downloadFile(docType, context.recipientId)
            .then((response: any) => {
                downloadFile(response.data, '', response.data.type);
            })
            .catch((error: IBaseResponse) => {
                toast.error("Unable to download file at this time.  Please contact support.");
            });
    };

    const handleViewDetails = (orderId: number) => {
        props.showLoading('Getting antibody screening results, one moment please...').then(() => {
            props.getAntibodyScreeningResult(context.centerId, context.recipientId, orderId)
                .then(response => {
                    if (response.data.success) {
                        dispatch({
                            type: 'viewDetails',
                            value: {
                                show: true,
                                data: response.data.results
                            }
                        });
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCloseDetails = () => {
        dispatch({
            type: 'viewDetails',
            value: {
                show: false,
                data: null
            }
        });
    };

    const handleProcessAntibodyScreeningOverride = (screeningId: number) => {
        if (confirm('Are you sure you want to over-ride this DnDSA?')) {
            props.showLoading('Processing override, one moment please...').then(() => {
                props.processAntibodyScreeningOverride(context.centerId, context.recipientId, screeningId)
                    .then(response => {
                        if (response.data.success) {
                            toast.success('Successfuly over-ridden a DnDSA.');
                        } else {
                            toast.error('Failed to over-ride a DnDSA.');
                        }
                    })
                    .finally(() => {
                        props.hideLoading();
                    });
            });
        }
    };

    const confirmFileUploads = async (afterAction: AfterAction) => {
        let errorUploads = [] as IFileUpload[];
        let blocked = false;

        props.showLoading('Saving records data, one moment please...').then(async () => {
            await Promise.all(
                state.files.map(async (fileUpload) => {
                    await props.uploadFile(context.centerId, context.recipientId, fileUpload.docType, fileUpload)
                        .then(response => {
                            if (!response.data.success) {
                                errorUploads.push(fileUpload);
                            }
                        })
                        .catch((error: any) => {
                            if (handleDuplicateError(error)) {
                                blocked = true;

                                return;
                            }

                            errorUploads.push(fileUpload);
                        })
                }))
                .then(async response => {
                    if (blocked) {
                        return;
                    }

                    if (errorUploads.length > 0) {
                        let msg = "Unable to upload: ";
                        errorUploads.forEach(x => msg += x.docType + ",");

                        toast.error(msg);
                    } else {
                        dispatch({
                            type: 'updateFiles',
                            value: []
                        });

                        context.updateHasChanges(false);
                        
                        getData();

                        toast.success("Upload successful.");
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        })
    };

    context.registerSave(props.saveRef, confirmFileUploads);

    if (context.headerInfo == null || state.data == null)
        return null;

    return (
        <>
            <ProfileWrapper>
                <Content
                    data={{ ...state.data, isRemoteDirect: context.headerInfo?.typeId == PatientType.RemoteDirect }}
                    files={state.files}
                    onViewFile={handleViewFile}
                    onFileUpload={handleFileUpload}
                    onRemoveFile={handleRemoveFile}
                />

                {context.headerInfo.isTransplanted && (
                    <Transplanted
                        list={state.antibodyScreenings}
                        onViewDetails={handleViewDetails}
                        onGetPdfFile={handleGetPdfFile}
                        onGetRawFile={handleGetRawFile} />
                )}
            </ProfileWrapper>

            <AntibodyScreeningDetails
                show={state.details.show}
                recipientAlias={context.headerInfo.alias}
                data={state.details.data}
                processAntibodyScreeningOverride={handleProcessAntibodyScreeningOverride}
                onClose={handleCloseDetails}
            />

            <DuplicateRecordModal
                show={showDuplicateRecord}
                message={duplicateMessage}
                onClose={handleCloseDuplicateRecord}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {
        app: appState.app
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecordsData: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getRecipientRecords(centerId, recipientId));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        uploadFile: (centerId, recipientId, docType, file) => {
            return dispatch(RecipientActionCreators.postRecipientRecords(centerId, recipientId, docType, file))
        },
        getAntibodyScreenings: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreenings(centerId, recipientId));
        },
        getAntibodyScreeningResult: (centerId, recipientId, screeningId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningResult(centerId, recipientId, screeningId));
        },
        getAntibodyScreeningPdfFile: (centerId, recipientId, orderId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningPdfFile(centerId, recipientId, orderId));
        },
        getAntibodyScreeningRawFile: (centerId, recipientId, orderId) => {
            return dispatch(RecipientActionCreators.getAntibodyScreeningRawFile(centerId, recipientId, orderId));
        },
        processAntibodyScreeningOverride: (centerId, recipientId, screeningId) => {
            return dispatch(RecipientActionCreators.processAntibodyScreeningOverride(centerId, recipientId, screeningId));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    mapStateToProps,
    mapDispatchToProps)(Records);